/**
 * A utils helper module to make the common functions available for all modules
 */
import _ from 'lodash-es'
import Constants from '@/helpers/Constants'
import type GuiMessage from '@/types/GuiMessage'
import MESSAGE_STATUS from '@/types/MessageStatus'
import EventBus from './EventBus'
import router from '@/router'
import store from '@/store'
import vm from '@/main'
import PB from '@/brokers/PersistencyBroker'


export default class Utils {

  /**
   * Navigate to a specific route
   * @param route Vue router route name
   */
  public static navigateTo(to: string, replace: boolean = false) {
    if (replace)
      router.replace(to)
    else
      router.push(to)
  }

  /**
   * Refresh/Reload the page
   */
  public static reloadPage() {
    window.location.reload()
  }


  /**
   * Clear an object entries
   */
  public static clearObject(obj: any) {
    if (Array.isArray(obj))
      obj = []

    if (typeof obj == 'object') {
      Object.keys(obj).forEach((key: any) => {
        if (typeof obj[key] == 'number')
          obj[key] = -1
        else if (typeof obj[key] == 'string')
          obj[key] = ''
        else if (Array.isArray(obj[key]))
          obj[key] = []
        else
          obj[key] = null
      })
    }
  }


  /**
   * Revers an array
   *
   * @param arr Array to be reversed
   * @returns Reversed array
   */
  public static reversArray(arr: Array<any>): Array<any> {
    return _.reverse(arr)
  }


  // ****************************************************************************************** Loading overlay controls
  public static hideLoading() {
    EventBus.emit(Constants.EVENTS.SHOW_LOADING, false)
  }
  public static showLoadingIndeterminate() {
    EventBus.emit(Constants.EVENTS.SHOW_LOADING, true)
  }
  public static showLoadingWithProgress(val: number) {
    EventBus.emit(Constants.EVENTS.SHOW_LOADING, val)
  }


  // **************************************************************************************** Global Messaging - Message


  // GUI message that will be used to communicate GUI messages to App.vue for global messaging
  // Declared as static to satisfy the static methods condition
  private static guiMessage: GuiMessage = {}

  /**
   * Hide all messages
   */
  public static hideMessages() {
    this.hideToasts()
  }
  /**
   * Show a simple success message
   *
   * Does not support formated message
   */
  public static showSuccessMessage(message: string, timeout: number = 5000): void {
    this.showToast(MESSAGE_STATUS.SUCCESS, message, timeout)
  }
  /**
   * Show a simple info message
   *
   * Does not support formated message
   */
  public static showInfoMessage(message: string, timeout: number = 5000): void {
    this.showToast(MESSAGE_STATUS.INFO, message, timeout)
  }
  /**
   * Show a simple warning message
   *
   * Does not support formated message
   */
  public static showWarningMessage(message: string, timeout: number = 5000): void {
    this.showToast(MESSAGE_STATUS.WARNING, message, timeout)
  }
  /**
   * Show a simple error message
   *
   * Does not support formated message
   */
  public static showErrorMessage(message: string, timeout: number = 5000): void {
    this.showToast(MESSAGE_STATUS.ERROR, message, timeout)
  }

  /**
   * Add a toast with a title and a message
   *
   * @param status info / error / warn / success
   * @param title Toast title
   * @param message Toast message
   */
  public static showToast(status: string | any, message: string, timeout: number = 5000) {
    vm.config.$toast.add({ severity: status, summary: Constants.APP_NAME, detail: message, life: timeout })
  }
  /**
   * Hide all toast messages
   */
  public static hideToasts() {
    vm.config.$toast.removeAllGroups()
  }


  // ********************************************************************************* Global Messaging - Message Dialog
  /**
   * Show an HTML formatted system message in a dialog box with a title
   *
   * Suports formatted message (HTML)
   */
  public static showSystemMessageDialog(title: string, message: string, showControls: boolean = true): void {
    this.showMessageDialog("primary", title, message, showControls)
  }
  /**
   * Show an HTML formatted success message in a dialog box with a title
   *
   * Suports formatted message (HTML)
   */
  public static showSuccessMessageDialog(title: string, message: string, showControls: boolean = true): void {
    this.showMessageDialog(MESSAGE_STATUS.SUCCESS, title, message, showControls)
  }
  /**
   * Show an HTML formatted info message in a dialog box with a title
   *
   * Suports formatted message (HTML)
   */
  public static showInfoMessageDialog(title: string, message: string, showControls: boolean = true): void {
    this.showMessageDialog(MESSAGE_STATUS.INFO, title, message, showControls)
  }
  /**
   * Show an HTML formatted warning message in a dialog box with a title
   *
   * Suports formatted message (HTML)
   */
  public static showWarningMessageDialog(title: string, message: string, showControls: boolean = true): void {
    this.showMessageDialog(MESSAGE_STATUS.WARNING, title, message, showControls)
  }
  /**
   * Show an HTML formatted error message in a dialog box with a title
   *
   * Suports formatted message (HTML)
   */
  public static showErrorMessageDialog(title: string, message: string, showControls: boolean = true): void {
    this.showMessageDialog(MESSAGE_STATUS.ERROR, title, message, showControls)
  }
  /**
   * A persistent messaging utility function
   *
   * Suports formatted message (HTML)
   */
  private static showMessageDialog(status: string, title: string, message: string, showControls: boolean = true): void {
    Utils.guiMessage.status = status
    Utils.guiMessage.title = title
    Utils.guiMessage.text = message
    Utils.guiMessage.showControls = showControls
    EventBus.emit(Constants.EVENTS.SHOW_MESSAGE_DIALOG, Utils.guiMessage)
  }
  /**
   * Hide the message dialog
   */
  public static hideMessageDialog(): void {
    EventBus.emit(Constants.EVENTS.SHOW_MESSAGE_DIALOG, false)
  }


  /**
   * Generate a random number of a given length.
   *
   * @param prefix A numeric value to indicate a specific operation.
   * @param length The length of the random number. Default is 10.
   * @returns The random number as a string.
   */
  public static getRandomNumber(pid: number): string {
    // Generate a random number with the specified length
    const length = Constants.PID_LENGTH[pid]
    const prefix = pid
    const min_value = Math.pow(10, length - 1)
    const max_value = Math.pow(10, length) - 1
    const prefixStr = prefix.toString().repeat(2)
    const randNumber = Math.floor(Math.random() * (max_value - min_value + 1) + min_value)
    return prefixStr + randNumber.toString()
  }

  /**
   * Generate a new PID
   * @param op The operation we are about to perform
   */
  public static generatePid(op: number) {
    const pid = this.getRandomNumber(op)
    store.dispatch('setPid', pid)
  }
  public static getPid(): string {
    return store.getters.getPid
  }


  public static getAppMode(): string {
    const appMode = PB.get(Constants.LOCAL_STORAGE_IDS.APP_MODE)
    if (appMode)
      return appMode
    else
      return 'Live'
  }
}
